import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { selectUploadState } from '../../store/slices/uploadProgressSlice';
import Tooltip from '../../../../components/Tooltip';
import classes from './styles.module.scss';

const CircularProgress = ({
  assetId,
  size = 48,
  strokeWidth = 4,
  onRetry,
}) => {
  const uploadState = useSelector(selectUploadState(assetId));
  const [displayProgress, setDisplayProgress] = useState(uploadState?.progress || 0);
  const [tooltipText, setTooltipText] = useState("The video is being processed");
  const animationFrameRef = useRef(null);
  const currentProgressRef = useRef(uploadState?.progress || 0);
  const tooltipTimerRef = useRef(null);

  // Smooth progress animation - more responsive
  useEffect(() => {
    if (!uploadState) {
      setDisplayProgress(0);
      currentProgressRef.current = 0;
      return () => {}; // Return empty cleanup function
    }

    const targetProgress = uploadState.progress || 0;

    // For immediate updates (large jumps or initialization), update directly
    const diff = Math.abs(targetProgress - currentProgressRef.current);
    if (diff > 5 || currentProgressRef.current === 0) {
      // Large change or initial state - update immediately
      currentProgressRef.current = targetProgress;
      setDisplayProgress(targetProgress);
      return () => {}; // Return empty cleanup function
    }

    // For small changes, use smooth interpolation
    function animate() {
      const currentDiff = targetProgress - currentProgressRef.current;
      if (Math.abs(currentDiff) < 0.5) {
        setDisplayProgress(targetProgress);
        currentProgressRef.current = targetProgress;
      } else {
        // More responsive interpolation (0.4 = faster catch-up)
        const newVal = currentProgressRef.current + currentDiff * 0.4;
        setDisplayProgress(newVal);
        currentProgressRef.current = newVal;
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [uploadState?.progress, uploadState]);

  // Handle extended tooltip timer for processing state
  useEffect(() => {
    const { status } = uploadState || {};
    const isProcessing = status === 'uploading' && Math.round(displayProgress) >= 100;

    // Clear any existing timer
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }

    // Reset tooltip text when not processing
    if (!isProcessing) {
      setTooltipText("The video is being processed");
    } else {
      // Set timer to show extended tooltip after 60 seconds
      tooltipTimerRef.current = setTimeout(() => {
        setTooltipText("Hang Tight! Video Still Processing.");
      }, 60000);
    }

    // Cleanup function
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
    };
  }, [uploadState?.status, displayProgress]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  if (!uploadState) {
    return null;
  }

  const { status } = uploadState;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  // Calculate stroke dash offset based on status
  let strokeDashoffset;
  if (status === 'uploading') {
    strokeDashoffset = circumference - (displayProgress / 100) * circumference;
  } else if (status === 'completed') {
    strokeDashoffset = 0;
  } else {
    strokeDashoffset = circumference;
  }

  const handleClick = () => {
    if (status === 'failed' && onRetry) {
      onRetry(assetId);
    }
  };

  // Determine stroke color based on status
  let strokeColor = '#F8585C'; // Brand pink/red for uploading
  if (status === 'completed') {
    strokeColor = '#F8585C'; // Brand pink/red for completed
  } else if (status === 'failed') {
    strokeColor = '#EF4444';
  }

  const showProcessingTooltip = status === 'uploading' && Math.round(displayProgress) >= 100;

  const progressElement = (
    <div
      className={`${classes.circularProgress} ${classes[status]}`}
      style={{ width: size, height: size }}
      onClick={handleClick}
      role={status === 'failed' ? 'button' : undefined}
      tabIndex={status === 'failed' ? 0 : undefined}
      onKeyDown={(e) => {
        if (status === 'failed' && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onRetry(assetId);
        }
      }}
    >
      {/* White background circle */}
      <div className={classes.whiteBackground} />
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={classes.svg}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={status === 'uploading' ? classes.progressCircle : ''}
          style={{
            transform: `rotate(-90deg)`,
            transformOrigin: `${size / 2}px ${size / 2}px`,
          }}
        />

        {/* Center content */}
        <g transform={`translate(${size / 2}, ${size / 2})`}>
          {(status === 'uploading' || status === 'queued') && (
            <>
              {status === 'queued' || Math.round(displayProgress) >= 100 ? (
                <g className={classes.animatedDots}>
                  <circle cx="-14" cy="0" r={(() => {
                    if (size > 80) return "6";
                    if (size > 40) return "5.5";
                    return "5";
                  })()} fill="#F8585C" className={classes.dot1} />
                  <circle cx="0" cy="0" r={(() => {
                    if (size > 80) return "6";
                    if (size > 40) return "5.5";
                    return "5";
                  })()} fill="#F8585C" className={classes.dot2} />
                  <circle cx="14" cy="0" r={(() => {
                    if (size > 80) return "6";
                    if (size > 40) return "5.5";
                    return "5";
                  })()} fill="#F8585C" className={classes.dot3} />
                </g>
              ) : (
                <text
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className={classes.progressText}
                  fontSize={(() => {
                    if (size > 80) return "24";
                    if (size > 40) return "12";
                    return "10";
                  })()}
                  fill="#111111"
                >
                  {Math.round(displayProgress)}%
                </text>
              )}
            </>
          )}

          {status === 'completed' && (
            <g className={classes.checkmark}>
              {/* Checkmark circle - sized to fit inside the progress ring */}
              {/* Inner radius accounts for stroke width to ensure it fits inside the ring */}
              <circle
                cx="0"
                cy="0"
                r={radius - strokeWidth}
                fill="#F8585C"
              />
              {/* Checkmark path - scaled proportionally based on circle size */}
              <path
                d={(() => {
                  const checkRadius = radius - strokeWidth;
                  // Scale checkmark to be 40% of the available radius
                  const scale = checkRadius * 0.4;
                  return `M-${scale * 0.4},0 L-${scale * 0.1},${scale * 0.3} L${scale * 0.4},-${scale * 0.3}`;
                })()}
                stroke="white"
                strokeWidth={Math.max(2, Math.round(strokeWidth * 0.6))}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          )}

          {status === 'failed' && (
            <g className={classes.error}>
              <circle
                cx="0"
                cy="0"
                r={radius - 2}
                fill="#EF4444"
              />
              <text
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={size > 80 ? "24" : "12"}
                fill="white"
                fontWeight="bold"
              >
                !
              </text>
            </g>
          )}
        </g>
      </svg>
    </div>
  );

  if (showProcessingTooltip) {
    return (
      <Tooltip text={tooltipText} position="top" width={220}>
        {progressElement}
      </Tooltip>
    );
  }

  return progressElement;
};

CircularProgress.propTypes = {
  assetId: PropTypes.string.isRequired,
  size: PropTypes.number,
  strokeWidth: PropTypes.number,
  onRetry: PropTypes.func,
};

CircularProgress.defaultProps = {
  onRetry: undefined,
  size: 48,
  strokeWidth: 4,
};

export default CircularProgress;
